'use client';
import * as actions from '@/actions';
import { MSProductWithFullDetailWithVaraintPreset } from '@/util/query/product-query';
import { toPriceString } from '@/util/toPriceString';
import { CUSTOMER_LEVEL } from '@prisma/client';
import { Loader2 } from 'lucide-react';
import { useSession } from 'next-auth/react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { memo, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
// import { CustomTooltip } from "../ui/Tooltip/custom-tooltip";
import { useTranslation } from '@/app/i18n/client';
import CartSvg from '@/assets/icons/cart.svg?react';
import CollectionListItemClient from '@/components/collections/collection-list-favorite-client';
import ProductTagRenderer from '@/components/product-tag-renderer';
import SmartImage from '@/components/SmartImage';
import { useCart } from '@/lib/providers/cart-provider';
import ColorStyleContext from '@/lib/providers/color-style-privider';
import { useLayoutStyle } from '@/lib/providers/layout-style-provider';
import { applyDiscountPromotion } from '@/util/applyPromotion';
import {
    getBestPriceByType,
    getPromotionDiscountedPrice,
    StepPriceType,
} from '@/util/promotion';
import { AddItemToCartRequestParams } from '@/util/services/cart/api';
import { formatCurrencyFormat } from '@/util/util';
import { useContext } from 'react';
import './product-card.css';
const CartNotification = dynamic(
    () => import('@/components/cart/cart-notification'),
    { ssr: false },
);

interface CollectionListItemProps {
    product: MSProductWithFullDetailWithVaraintPreset;
    href: string;
    // t: TFunction<"translation", undefined>;
    level: CUSTOMER_LEVEL;
    refreshfav?: boolean;
    variantId?: string;
    priorityImage?: boolean;
    shopCurrency: string;
    favoriteEnable: boolean;
    /** The compact mobile card CSS (tighter padding/gap/font-size) only applies when this is 3. */
    mobileColumn?: number;
}

// Helper component to render currency smaller than the numeric price.
// shopCurrency format example: "฿ {price}" or "{price} ฿" (always contains "{price}").
// We split around {price} and render currency parts with smaller font-size.
interface CurrencyPriceProps {
    priceString: string; // already formatted numeric string (e.g., 230 or 1,250.00)
    shopCurrency: string; // pattern with {price}
    wrapperClassName?: string; // classes applied to outer wrapper
    priceClassName?: string; // classes for the numeric part
    currencyClassName?: string; // classes for currency part (defaults to smaller text)
    id?: string;
}

const CurrencyPrice = ({
    priceString,
    shopCurrency,
    wrapperClassName = '',
    priceClassName = '',
    currencyClassName = 'text-sm',
    id,
}: CurrencyPriceProps) => {
    const parts = shopCurrency.split('{price}');
    const prefix = parts[0] ? parts[0].trim() : '';
    const suffix = parts[1] ? parts[1].trim() : '';
    return (
        <span
            id={id}
            className={`inline-flex items-baseline gap-1 ${wrapperClassName}`}
        >
            {prefix && <span className={currencyClassName}>{prefix}</span>}
            <span className={priceClassName}>{priceString}</span>
            {suffix && <span className={currencyClassName}>{suffix}</span>}
        </span>
    );
};

const ProductCardGradientInner = ({
    product,
    href,
    // t,
    level,
    refreshfav,
    variantId,
    priorityImage,
    shopCurrency,
    favoriteEnable,
    mobileColumn,
}: CollectionListItemProps) => {
    // The compact mobile card CSS (Figma spec) only applies when 3 GRADIENT columns
    // are shown on mobile; otherwise mobile falls back to the original spacious layout.
    const useTightMobileLayout = mobileColumn === 3;
    // Equalize heights of the two dynamic blocks across all product cards so layout rows align.
    useEffect(() => {
        const syncHeights = () => {
            const infoBlocks = Array.from(
                document.querySelectorAll<HTMLDivElement>(
                    '#product-card-gradient-info .product-item-next-step-info',
                ),
            );
            const normalBlocks = Array.from(
                document.querySelectorAll<HTMLDivElement>(
                    '#product-card-gradient-info .product-item-next-step-info-normal',
                ),
            );
            // Reset heights to natural first
            infoBlocks.forEach((el) => (el.style.height = 'auto'));
            normalBlocks.forEach((el) => (el.style.height = 'auto'));
            const maxInfo = infoBlocks.reduce(
                (m, el) => Math.max(m, el.offsetHeight),
                0,
            );
            const maxNormal = normalBlocks.reduce(
                (m, el) => Math.max(m, el.offsetHeight),
                0,
            );
            infoBlocks.forEach((el) => (el.style.height = maxInfo + 'px'));
            normalBlocks.forEach((el) => (el.style.height = maxNormal + 'px'));
        };
        // Run after mount & on images load slight delay
        const timeout = setTimeout(syncHeights, 50);
        window.addEventListener('resize', syncHeights);
        return () => {
            clearTimeout(timeout);
            window.removeEventListener('resize', syncHeights);
        };
    }, []);
    const { t, loc } = useTranslation();
    const inStockVariants = product.productVariant.filter(
        (v) => v.orderType !== 'DISABLED' && v.inventory - v.inventory_used > 0,
    );
    const defaultVariant =
        inStockVariants.length > 0
            ? inStockVariants.reduce((a, b) =>
                  a.priceFull < b.priceFull ? a : b,
              )
            : product.productVariant[0] ?? null;
    let variant = product.productVariant.length > 0 ? defaultVariant : null;
    if (variantId) {
        const variantsFilter = product.productVariant.filter(
            (x) => x.id === variantId,
        );
        if (variantsFilter.length > 0) {
            variant = variantsFilter[0];
        }
    }
    const title = product.attributes?.displayName ?? '';
    let cv: number | null;
    let qv: number | null;
    if (level === 'BP_VVIP') {
        cv = variant ? variant.CV_VVIP : null;
        qv = variant ? variant.QV_VVIP : null;
    } else if (level === 'BP_VIP') {
        cv = variant ? variant.CV_VIP : null;
        qv = variant ? variant.QV_VIP : null;
    } else {
        cv = variant ? variant.CV : null;
        qv = variant ? variant.QV : null;
    }

    const promotionDiscountedPrice = variant
        ? getPromotionDiscountedPrice(variant)
        : ({} as any);
    const rawStepTiers = promotionDiscountedPrice.STEP_PRICE?.value; // StepPriceValue[]
    // For collection list we assume quantity in cart = 0 (preview). So currentStep will be undefined and nextStep first tier.
    const stepSelection: StepPriceType | null = rawStepTiers
        ? (getBestPriceByType(rawStepTiers, 'STEP_PRICE', 0) as StepPriceType)
        : null;
    const nextStep = stepSelection?.nextStep;

    // Apply Discount Promotion price
    variant = applyDiscountPromotion(promotionDiscountedPrice, variant);

    // Effective special price for display. Step price is a special price: apply it
    // only when it beats the existing special/normal price. Kept in locals (we do NOT
    // mutate variant) so re-renders never compound the discount.
    let displayCompareFull = variant?.comparePriceFull ?? null;
    let displayCompareTrial = variant?.comparePriceTrial ?? null;
    if (variant && nextStep) {
        const stepFull = nextStep.price['GUEST']?.price;
        const stepTrial = nextStep.price['BP']?.price;
        if (stepFull != null)
            displayCompareFull = Math.min(
                stepFull,
                displayCompareFull ?? variant.priceFull,
            );
        if (stepTrial != null)
            displayCompareTrial = Math.min(
                stepTrial,
                displayCompareTrial ?? variant.priceTrial,
            );
    }

    const trialNormalPrice = variant ? variant.priceTrial : null;
    const normalPrice = variant ? variant.priceFull : null;
    // Base the step discount is measured from: special price when set & lower
    // (same base as getPromotionDiscountedPrice), else the list price.
    const stepNormalPrice = !variant
        ? 0
        : level === 'GUEST'
          ? variant.comparePriceFull != null &&
            variant.comparePriceFull < variant.priceFull
              ? variant.comparePriceFull
              : variant.priceFull
          : variant.comparePriceTrial != null &&
              variant.comparePriceTrial < variant.priceTrial
            ? variant.comparePriceTrial
            : variant.priceTrial;
    const isTrialHaveSpecialPrice =
        variant && displayCompareTrial != null
            ? displayCompareTrial < variant.priceTrial
            : false;
    const isFullHaveSpecialPrice =
        variant && displayCompareFull != null
            ? displayCompareFull < variant.priceFull
            : false;
    const trialPrice = isTrialHaveSpecialPrice
        ? displayCompareTrial
        : variant
          ? variant.priceTrial
          : null;
    const price = isFullHaveSpecialPrice
        ? displayCompareFull
        : variant
          ? variant.priceFull
          : null;

    var imageUrl = product.featuredImageUrl
        ? product.featuredImageUrl
        : (variant ? variant.featuredImageUrl : '') || '';

    const imageIndex = product.imagesUrl.findIndex(
        (image) => image === imageUrl,
    );
    const imageAlt = product.imagesAlt[imageIndex] || `product-${product.id}`;

    const colorStyle = useContext(ColorStyleContext);

    const session = useSession();
    const { addItem, getQuantityForItem } = useCart({ loc: 'th' });
    const router = useRouter();
    const layoutStyle = useLayoutStyle();

    const [requireQuantity, setRequireQuantity] = useState<number>(1);
    const [isAddingItem, setAddingItem] = useState<boolean>(false);

    const orderType = variant?.orderType ?? 'DISABLED';
    const isOutOfStock = variant
        ? variant.inventory - variant.inventory_used <= 0
        : false;
    const quantityInCart = variant ? getQuantityForItem(variant.id) : 0;
    const stock = variant ? variant.inventory - variant.inventory_used : 0;
    const isAddAllStockInCart = quantityInCart >= stock && stock > 0;

    let text: { [key: string]: string } = {};
    text['weight_2'] = t('product.weight_2');
    text['share_btn_share'] = t('share.btn_share');
    text['share_recommend_business'] = t('share.recommend_business');
    text['share_open_opportunities'] = t('share.open_opportunities');
    text['product_promotion_special'] = t('product.promotion_special');
    text['product_out_of_stock'] = t('product.out_of_stock');
    text['product_all_stock_incart'] = t('product.all_stock_incart');
    text['product_btn_register'] = t('product.btn_register');
    text['product_delivery_fee'] = t('product.delivery_fee');
    text['product_delivery_fee_condition'] = t(
        'product.delivery_fee_condition',
    );
    text['product_sold_out'] = t('product.sold_out');
    text['product_coming_soon'] = t('product.coming_soon');
    text['product_vvip_subscription_price'] = t(
        'product.vvip_subscription_price',
    );
    text['product_vip_subscription_price'] = t(
        'product.vip_subscription_price',
    );
    text['product_trial_price'] = t('product.trial_price');
    text['product_price'] = t('product.price');
    text['product_cv'] = t('product.cv');
    text['product_qv'] = t('product.qv');
    text['product_trial_tip'] = t('product.trial_tip');
    text['quantity'] = t('quantity');
    text['product_btn_add_to_quote'] = t('product.add_to_quote');
    text['product_btn_buy_trial'] = t('product.btn_buy_trial');
    text['product_btn_buy_1'] = t('product.btn_buy_1');
    text['product_btn_compare'] = t('product.product_btn_compare');
    text['cart_add_to_cart'] = t('cart.add_to_cart');
    text['cart_add_to_quote'] = t('cart.add_to_quote');
    text['cart_btn_view_cart2'] = t('cart.btn_view_cart2');
    text['cart_btn_view_quote_cart2'] = t('cart.btn_view_quote_cart2');
    text['cart_btn_checkout'] = t('cart.btn_checkout');
    text['cart_btn_create_quote'] = t('cart.btn_create_quote');
    text['product_specification_title'] = t('product.product_information');
    text['all_products_available'] = t('product.all_products_available');
    text['view_product_detail'] = t('product.go_to_product_deatil');
    text['product_image'] = t('tab_navigator.product_image');
    text['product_name'] = t('tab_navigator.product_name');
    text['compare'] = t('tab_navigator.compare');
    text['product_link'] = t('tab_navigator.product_link');
    text['go_to_form'] = t('product.go_to_form');
    text['load_more'] = t('tab_navigator.load_more');
    text['select'] = t('tab_navigator.select');
    text['select_all'] = t('tab_navigator.select_all');

    const handleOnAddCart = async () => {
        const user = await actions.getUser();
        if (!variant) {
            return;
        }

        const productToAdd = {
            idProduct: product.id,
            idVariant: variant.id,
            quantity: requireQuantity,
        };
        if (!user && session.status === 'authenticated') {
            await actions.createGuest(false);
        }
        if (session.status === 'authenticated') {
            await addItemToCart(productToAdd);
        } else if (session.status === 'unauthenticated') {
            await actions.createGuest(false);
            await addItemToCart(productToAdd);
            setTimeout(() => window.location.reload(), 1000);
        }
    };

    const addItemToCart = async (productToAdd: AddItemToCartRequestParams) => {
        if (!variant) {
            return;
        }
        setAddingItem(true);

        try {
            await addItem(productToAdd);

            setAddingItem(false);
            toast.custom(
                (t) => (
                    <CartNotification
                        visible={t.visible}
                        onClose={() => toast.remove(t.id)}
                        product={product}
                        variant={variant}
                        type={
                            level === 'BP_VVIP'
                                ? 'vvip'
                                : level === 'BP_VIP'
                                  ? 'vip'
                                  : level === 'BP'
                                    ? 'trial'
                                    : level === 'NORMAL'
                                      ? 'trial'
                                      : 'normal'
                        }
                        loc={loc}
                        text={text}
                        shopCurrency={shopCurrency}
                        color={colorStyle}
                        radiusStyle={layoutStyle}
                    />
                ),
                { position: 'top-right', duration: 2000 },
            );
            router.refresh();
        } catch (error: any) {
            setAddingItem(false);
            toast.error(error.message, { position: 'bottom-center' });
        }
    };

    return (
        <div
            id={`product-card-gradient-style-${product.id}`}
            className={`flex flex-col ${useTightMobileLayout ? 'gap-2 p-1 pb-2' : 'gap-4 p-2 pb-4'} sm:gap-4 justify-start items-start w-full h-full sm:p-2 sm:pb-4 rounded-[10px] text-[#54301A]`}
        >
            <div
                id="product-card-gradient-image-container"
                className="relative w-full flex justify-center items-center aspect-square min-h-240 sm:min-h-480"
                style={{ maxWidth: '100%' }}
            >
                <a
                    id="product-card-gradient-link"
                    href={href}
                    className="w-full h-full"
                >
                    <SmartImage
                        id="product-item-img"
                        src={imageUrl}
                        alt={imageAlt}
                        width={480}
                        height={480}
                        className="h-full w-full object-contain bg-white rounded-lg"
                        loading={priorityImage ? 'eager' : 'lazy'}
                    />
                </a>
                {/* <div className="p-1 bg-white rounded-full w-7 h-7 absolute top-2 right-2"> */}
                {/* <span className="material-symbols-outlined text-lg w-full text-center">favorite</span> */}
                {favoriteEnable && (
                    <CollectionListItemClient
                        productHandle={product.handle}
                        refresh={refreshfav}
                        className={
                            useTightMobileLayout
                                ? 'w-6 h-6 md:w-10 md:h-10 absolute sm:top-4 sm:right-4 top-2 right-2 bg-white'
                                : undefined
                        }
                        iconClassName={
                            useTightMobileLayout
                                ? 'w-[13px] h-[13px] sm:w-[18px] sm:h-[18px]'
                                : undefined
                        }
                    />
                )}
                {/* </div> */}
            </div>
            <div
                id="product-card-gradient-info"
                className={`flex flex-col ${useTightMobileLayout ? 'gap-2' : 'gap-5'} sm:gap-5 justify-between w-full h-full`}
            >
                <div
                    id={`product-item-title-container`}
                    className={`${useTightMobileLayout ? 'min-h-0' : 'min-h-16'} sm:min-h-16 p-1`}
                >
                    <a
                        id={`product-item-link`}
                        href={href}
                        className={`block font-semibold ${useTightMobileLayout ? 'text-[10px] leading-[12px] min-h-0' : 'text-lg leading-8 min-h-16'} sm:text-lg sm:leading-8 sm:min-h-16 hover:opacity-50 text-[#54301A] w-full`}
                    >
                        <span
                            id={`product-item-title`}
                            className={`line-clamp-3 ${useTightMobileLayout ? 'text-[10px] leading-[14px] min-h-0' : 'text-lg leading-8 min-h-16'} sm:text-lg sm:leading-8 sm:min-h-16`}
                        >
                            {title}
                        </span>
                    </a>
                </div>
                <div
                    id="product-card-gradient-info-bottom"
                    className="flex flex-col gap-5 justify-end w-full h-full"
                >
                    <ProductTagRenderer
                        tags={product.tags}
                        iconOnly
                        compactMobile={useTightMobileLayout}
                    />
                    {
                        // if price contain key of "STEP_PRICE"
                        nextStep && variant?.show_price ? (
                            <div
                                id={`product-item-next-step`}
                                className="w-full flex flex-col"
                            >
                                <div
                                    id={`product-item-next-step-info`}
                                    className="product-item-next-step-info w-full text-[#ED1C24] p-[10px] bg-[#FFF200] rounded-t-lg"
                                >
                                    <div
                                        id={`product-item-next-step-info-text`}
                                        className="w-full flex flex-col items-start justify-end"
                                    >
                                        <span
                                            id={`product-item-next-step-info-quantity`}
                                            className={`${
                                                useTightMobileLayout
                                                    ? 'text-[8px] leading-[12px]'
                                                    : 'text-sm'
                                            } sm:text-sm`}
                                        >
                                            {t('promotion.step_price_steps', {
                                                step: nextStep.step,
                                            })}
                                        </span>
                                        <span
                                            id={`product-item-next-step-info-price`}
                                            className={`w-full flex flex-col gap-2 items-start font-semibold xl:text-3xl lg:text-2xl ${
                                                useTightMobileLayout
                                                    ? 'text-[14px] leading-[19px]'
                                                    : 'text-xl leading-8'
                                            } sm:text-xl sm:leading-8`}
                                        >
                                            {nextStep.price[level] !==
                                                undefined && (
                                                <CurrencyPrice
                                                    id={`product-item-next-step-info-price-value`}
                                                    priceString={toPriceString(
                                                        nextStep.price[level]!
                                                            .price,
                                                    )}
                                                    shopCurrency={shopCurrency}
                                                    wrapperClassName="font-semibold"
                                                    priceClassName={`xl:text-[30px] lg:text-[24px] ${
                                                        useTightMobileLayout
                                                            ? 'text-[14px] leading-[19px]'
                                                            : 'text-[20px] leading-8'
                                                    } sm:text-[20px] sm:leading-8`}
                                                    currencyClassName={`${
                                                        useTightMobileLayout
                                                            ? 'text-[8px]'
                                                            : 'text-sm'
                                                    } sm:text-sm`}
                                                />
                                            )}
                                            <div
                                                className={`w-full flex flex-row gap-2 justify-between ${
                                                    useTightMobileLayout
                                                        ? 'h-[26px]'
                                                        : 'h-[37px]'
                                                } sm:h-[37px]`}
                                            >
                                                <span
                                                    id={`product-item-next-step-info-per-item`}
                                                    className={`xl:leading-5 ${
                                                        useTightMobileLayout
                                                            ? 'text-[8px] leading-[12px]'
                                                            : 'text-xs leading-6'
                                                    } sm:text-xs sm:leading-6`}
                                                >
                                                    {t('promotion.per_item', {
                                                        step: nextStep.step,
                                                    })}
                                                </span>
                                                {orderType !== 'DISABLED' &&
                                                    orderType ===
                                                        'BUY_BUTTON' &&
                                                    product.sell_state ==
                                                        'READY_FOR_SELL' &&
                                                    !isOutOfStock &&
                                                    !isAddAllStockInCart &&
                                                    variant && (
                                                        <button
                                                            id={`product-item-next-step-info-button`}
                                                            aria-label="Add to cart"
                                                            onClick={
                                                                handleOnAddCart
                                                            }
                                                            className={`group rounded-full border border-[#ED1C24] hover:border-[#FFC20E] ${
                                                                useTightMobileLayout
                                                                    ? 'p-[6px]'
                                                                    : 'p-2'
                                                            } sm:p-2 flex items-center justify-center cursor-pointer flex-grow-0 h-auto self-end bg-[#ED1C24] hover:bg-[#FFC20E]`}
                                                        >
                                                            {isAddingItem ? (
                                                                <Loader2
                                                                    className={`${
                                                                        useTightMobileLayout
                                                                            ? 'w-[14px] h-[14px]'
                                                                            : 'w-[20px] h-[20px]'
                                                                    } sm:w-[20px] sm:h-[20px] animate-spin`}
                                                                />
                                                            ) : (
                                                                <CartSvg
                                                                    className={`text-[#FFFFFF] group-hover:text-[#54301A] transition-colors ${
                                                                        useTightMobileLayout
                                                                            ? 'w-[14px] h-[14px]'
                                                                            : 'w-[20px] h-[20px]'
                                                                    } sm:w-[20px] sm:h-[20px]`}
                                                                    id={`product-item-next-step-info-button-icon`}
                                                                />
                                                            )}
                                                        </button>
                                                    )}
                                            </div>
                                        </span>
                                    </div>
                                </div>
                                <div className="product-item-next-step-info-normal rounded-b-lg w-full bg-[#E21E06] text-white p-[10px]">
                                    <div
                                        id={`product-item-next-step-info-normal`}
                                        className="w-full flex flex-wrap flex-row  gap-px justify-between items-start"
                                    >
                                        <span
                                            id={`product-item-next-step-info-normal-price`}
                                            className="flex flex-col gap-px font-normal text-white"
                                        >
                                            <span style={{ fontSize: useTightMobileLayout ? '8px':'10px' }}>
                                                {t('product.normal')}
                                            </span>
                                            <span className="flex flex-row gap-2 items-end">
                                                <span className={`line-through xl:text-base lg:text-sm ${useTightMobileLayout ? `text-[8px]`:`text-xs`}`}>
                                                    {formatCurrencyFormat(
                                                        toPriceString(
                                                            stepNormalPrice,
                                                        ),
                                                        shopCurrency,
                                                    )}
                                                </span>
                                                <span
                                                    id={`product-item-next-step-info-per-item`}
                                                    style={{ fontSize: useTightMobileLayout ?'8px':'10px' }}
                                                >
                                                    {t('promotion.per_item', {
                                                        step: nextStep.step,
                                                    })}
                                                </span>
                                            </span>
                                        </span>
                                        <span
                                            id={`product-item-next-step-info-normal-price-percentage`}
                                            className={`${useTightMobileLayout ? `text-[8px]`:'text-xs'} font-semibold  self-end`}
                                        >
                                            {t('promotion.saved_percentage', {
                                                percentage: Math.floor(
                                                    nextStep.price[level]
                                                        ?.percentage || 0,
                                                ),
                                            })}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ) : null
                    }
                    {variant?.show_price && !nextStep && (
                        <div>
                            <div
                                id={`product-item-price`}
                                className="flex flex-col w-full"
                            >
                                {/* BP Price */}
                                {level === 'BP' && (
                                    <div
                                        id={`product-item-bp-price`}
                                        className="flex justify-between items-bottom w-full"
                                    >
                                        <div
                                            id={`product-item-bp-price-info`}
                                            className="flex flex-col flex-wrap justify-end"
                                        >
                                            {isTrialHaveSpecialPrice &&
                                                trialNormalPrice && (
                                                    <span
                                                        id={`product-item-price-normal`}
                                                        className={`line-through font-normal ${useTightMobileLayout ? 'text-[12px] leading-[12px] pb-0' : 'text-base leading-8 pb-[2px]'} sm:text-base sm:leading-8 text-[#54301A]`}
                                                    >
                                                        {formatCurrencyFormat(
                                                            toPriceString(
                                                                trialNormalPrice,
                                                            ),
                                                            shopCurrency,
                                                        )}
                                                    </span>
                                                )}
                                            {trialPrice !== null &&
                                                trialPrice !== undefined && (
                                                    <span
                                                        id={`product-item-price-special`}
                                                        className={`font-semibold ${useTightMobileLayout ? 'text-[14px] leading-[17px]' : 'text-xl leading-8'} sm:text-xl sm:leading-8 ${
                                                            isTrialHaveSpecialPrice &&
                                                            trialNormalPrice
                                                                ? 'text-[#ED1C24]'
                                                                : 'text-[#54301A]'
                                                        }`}
                                                    >
                                                        {formatCurrencyFormat(
                                                            toPriceString(
                                                                trialPrice,
                                                            ),
                                                            shopCurrency,
                                                        )}
                                                    </span>
                                                )}
                                        </div>
                                        {orderType !== 'DISABLED' &&
                                            orderType === 'BUY_BUTTON' &&
                                            product.sell_state ==
                                                'READY_FOR_SELL' &&
                                            !isOutOfStock &&
                                            !isAddAllStockInCart &&
                                            variant && (
                                                <button
                                                    id={`product-item-bp-price-info-button`}
                                                    aria-label={
                                                        product.isSubscriptionProduct
                                                            ? 'View product detail'
                                                            : 'Add to cart'
                                                    }
                                                    onClick={
                                                        product.isSubscriptionProduct
                                                            ? () =>
                                                                  router.push(
                                                                      href,
                                                                  )
                                                            : handleOnAddCart
                                                    }
                                                    className={`group rounded-full border border-[#54301A] hover:border-[#F3C615] ${
                                                        useTightMobileLayout
                                                            ? 'p-[6px]'
                                                            : 'p-2'
                                                    } sm:p-2 flex items-center justify-center cursor-pointer flex-grow-0 h-auto self-end hover:bg-[#FFC20E]`}
                                                >
                                                    {!product.isSubscriptionProduct &&
                                                    isAddingItem ? (
                                                        <Loader2
                                                            className={`${
                                                                useTightMobileLayout
                                                                    ? 'w-[14px] h-[14px]'
                                                                    : 'w-[20px] h-[20px]'
                                                            } sm:w-[20px] sm:h-[20px] animate-spin`}
                                                        />
                                                    ) : (
                                                        <CartSvg
                                                            id={`bp-price-info-cart-icon`}
                                                            color="#54301A"
                                                            className={`${
                                                                useTightMobileLayout
                                                                    ? 'w-[14px] h-[14px]'
                                                                    : 'w-[20px] h-[20px]'
                                                            } sm:w-[20px] sm:h-[20px]`}
                                                        />
                                                    )}
                                                </button>
                                            )}
                                    </div>
                                )}
                                {/* Normal Price */}
                                {level === 'NORMAL' ||
                                    (level === 'GUEST' && (
                                        <div
                                            id={`product-item-normal-price`}
                                            className="flex justify-between items-bottom w-full"
                                        >
                                            <div
                                                id={`product-item-normal-price-info`}
                                                className="flex flex-col flex-wrap justify-end"
                                            >
                                                {isFullHaveSpecialPrice &&
                                                    normalPrice && (
                                                        <span
                                                            id={`product-item-price-normal`}
                                                            className={`line-through font-normal ${useTightMobileLayout ? 'text-[12px] leading-[12px] pb-0' : 'text-base leading-8 pb-[2px]'} sm:text-base sm:leading-8 text-[#3d3d3d]`}
                                                        >
                                                            {formatCurrencyFormat(
                                                                toPriceString(
                                                                    normalPrice,
                                                                ),
                                                                shopCurrency,
                                                            )}
                                                        </span>
                                                    )}
                                                {price !== null &&
                                                    price !== undefined && (
                                                        <span
                                                            id={`product-item-price-special`}
                                                            className={`font-semibold ${useTightMobileLayout ? 'text-[14px] leading-[17px]' : 'text-xl leading-8'} sm:text-xl sm:leading-8 ${
                                                                isFullHaveSpecialPrice &&
                                                                normalPrice
                                                                    ? 'text-[#ED1C24]'
                                                                    : 'text-[#54301A]'
                                                            }`}
                                                        >
                                                            {formatCurrencyFormat(
                                                                toPriceString(
                                                                    price,
                                                                ),
                                                                shopCurrency,
                                                            )}
                                                        </span>
                                                    )}
                                            </div>
                                            {orderType !== 'DISABLED' &&
                                                orderType === 'BUY_BUTTON' &&
                                                product.sell_state ==
                                                    'READY_FOR_SELL' &&
                                                !isOutOfStock &&
                                                !isAddAllStockInCart &&
                                                variant && (
                                                    <button
                                                        id={`product-item-normal-price-info-button`}
                                                        aria-label="Add to cart"
                                                        onClick={
                                                            handleOnAddCart
                                                        }
                                                        className={`group rounded-full border border-[#54301A] hover:border-[#F3C615] ${useTightMobileLayout ? 'p-[6px]' : 'p-2'} sm:p-2 flex items-center justify-center cursor-pointer flex-grow-0 h-auto self-end hover:bg-[#FFC20E]`}
                                                    >
                                                        {isAddingItem ? (
                                                            <Loader2 className={`${useTightMobileLayout ? 'w-[14px] h-[14px]' : 'w-[20px] h-[20px]'} sm:w-[20px] sm:h-[20px] animate-spin`} />
                                                        ) : (
                                                            <CartSvg
                                                                id={`normal-price-info-cart-icon`}
                                                                color="#54301A"
                                                                className={`${useTightMobileLayout ? 'w-[14px] h-[14px]' : 'w-[20px] h-[20px]'} sm:w-[20px] sm:h-[20px]`}
                                                            />
                                                        )}
                                                    </button>
                                                )}
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Comparator: re-render only if key props or core product pricing/tag info change.
// (Conservative: if unsure, return false to allow update.)
const areEqual = (
    prev: CollectionListItemProps,
    next: CollectionListItemProps,
) => {
    if (prev === next) return true;
    if (prev.href !== next.href) return false;
    if (prev.level !== next.level) return false;
    if (prev.variantId !== next.variantId) return false;
    if (prev.priorityImage !== next.priorityImage) return false;
    if (prev.shopCurrency !== next.shopCurrency) return false;
    if (prev.refreshfav !== next.refreshfav) return false;
    if (prev.mobileColumn !== next.mobileColumn) return false;
    const pp = prev.product;
    const np = next.product;
    if (pp === np) return true; // same reference
    if (!pp || !np) return false;
    if (pp.id !== np.id) return false;
    // Compare basic displayed fields
    if (pp.attributes?.displayName !== np.attributes?.displayName) return false;
    // Compare variant counts & first variant core pricing (most cards show first variant)
    const pVar0 = pp.productVariant?.[0];
    const nVar0 = np.productVariant?.[0];
    if (!!pVar0 !== !!nVar0) return false;
    if (pVar0 && nVar0) {
        if (pVar0.id !== nVar0.id) return false;
        if (pVar0.priceFull !== nVar0.priceFull) return false;
        if (pVar0.comparePriceFull !== nVar0.comparePriceFull) return false;
        if (pVar0.priceTrial !== nVar0.priceTrial) return false;
        if (pVar0.comparePriceTrial !== nVar0.comparePriceTrial) return false;
    }
    // Compare tag ids (order-insensitive) length + ids set
    const pTags = pp.tags || [];
    const nTags = np.tags || [];
    if (pTags.length !== nTags.length) return false;
    for (let i = 0; i < pTags.length; i++) {
        if (pTags[i].id !== nTags[i].id) return false; // assumes stable ordering
    }
    return true; // no significant changes detected
};

const Loading = () => {
    return (
        <div className="flex flex-col gap-4 justify-start items-start w-full h-full bg-gradient-to-b from-[#FFC20E] to-white p-2 pb-4 rounded-[10px] animate-pulse">
            <div
                className="relative w-full flex justify-center items-center aspect-square min-h-240 sm:min-h-480 bg-[#FFC20E] opacity-50"
                style={{ maxWidth: '100%' }}
            >
                <div className="h-full w-full   rounded-3xl" />
            </div>
            <div className="flex flex-col gap-5 justify-between w-full h-full">
                <div className="h-12 w-full bg-[#FFC20E] opacity-50 rounded" />
                <div className="flex gap-2 items-center">
                    <div className="h-6 w-6 bg-[#FFC20E] opacity-50 rounded-full" />
                    <div className="h-6 w-6 bg-[#FFC20E] opacity-50 rounded-full" />
                    <div className="h-6 w-6 bg-[#FFC20E] opacity-50 rounded-full" />
                </div>
                <div className="h-6 w-1/4 bg-[#FFC20E] rounded" />
                <div className="h-6 w-1/3 bg-[#FFC20E] rounded" />
            </div>
        </div>
    );
};

const ProductCardGradient = memo(ProductCardGradientInner, areEqual);

export default dynamic(() => Promise.resolve(ProductCardGradient), {
    ssr: false,
    loading: Loading,
});
